import { useState, type FormEvent } from "react";
import { Link } from "wouter";
import { api, ApiError } from "../../api";
import { ErrorNote, Loading, timeAgo, useApi } from "../../components/ui";

export function StaffSettings() {
  return (
    <div className="page stack">
      <h1>Settings</h1>
      <SampleData />
      <GitHub />
      <Discord />
      <ServiceKeys />
      <Webhooks />
      <section className="card stack">
        <h2>API</h2>
        <p style={{ margin: 0 }}>
          Everything in the portal is available at <code className="mono">/api/v1</code>. Create a personal token under{" "}
          <Link href="/account">your account</Link> and send it as <code className="mono">Authorization: Bearer dlt_…</code>. DL apps push in-app feedback with
          per-app keys (Customers → Apps). The full reference is <code className="mono">docs/api.md</code> in the support-portal repo.
        </p>
      </section>
    </div>
  );
}

function GitHub() {
  const { data, error, loading, reload } = useApi<{
    configured: boolean;
    org: string;
    repos: string[];
    lastSync: { at: string; repos: number; created: number; updated: number; error?: string; errorAt?: string } | null;
    error: string | null;
  }>("/staff/github");
  const [busy, setBusy] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const sync = async () => {
    setBusy(true);
    setSyncError(null);
    try {
      await api("/staff/github/sync", { method: "POST" });
      reload();
    } catch (e) {
      setSyncError(e instanceof ApiError ? e.message : "Sync failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card stack">
      <div className="card-head" style={{ marginBottom: 0 }}>
        <h2>GitHub</h2>
        {data?.configured && (
          <button className="btn btn-small" onClick={sync} disabled={busy}>
            {busy ? "Syncing…" : "Sync now"}
          </button>
        )}
      </div>
      <ErrorNote message={error ?? syncError ?? data?.error ?? null} />
      {loading && !data ? (
        <Loading />
      ) : !data?.configured ? (
        <p className="muted" style={{ margin: 0 }}>
          Not connected. Create the GitHub App for the {data?.org ?? "Departure-Labs"} org and set the GITHUB_APP_ID, GITHUB_APP_PRIVATE_KEY and GITHUB_WEBHOOK_SECRET secrets (see docs/deploy.md).
        </p>
      ) : (
        <>
          <p style={{ margin: 0 }}>
            Connected to <strong>{data.org}</strong> · {data.repos.length} repositories visible. Issues sync every 30 minutes and live through the webhook.
          </p>
          {data.lastSync && (
            <p className="small muted" style={{ margin: 0 }}>
              Last sync {timeAgo(data.lastSync.at)}: {data.lastSync.repos} repos, {data.lastSync.created} new, {data.lastSync.updated} updated.
              {data.lastSync.error && <strong style={{ color: "var(--danger)" }}> Last error {timeAgo(data.lastSync.errorAt)}: {data.lastSync.error}</strong>}
            </p>
          )}
          <p className="small muted" style={{ margin: 0 }}>
            Priority labels (P0–P3, "priority: high", "urgent") and size labels ("size: S", "good first issue") set priority and effort automatically. Link a repo to a customer under Customers → Apps.
          </p>
        </>
      )}
    </section>
  );
}

interface Hook {
  id: string;
  url: string;
  events: string[];
  description: string;
  active: number;
  last_status: number | null;
  last_error: string | null;
  last_delivery_at: string | null;
}

function Webhooks() {
  const { data, error, reload } = useApi<{ webhooks: Hook[]; events: string[] }>("/staff/webhooks");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [events, setEvents] = useState<string[]>(["*"]);
  const [secret, setSecret] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, string>>({});

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      const r = await api<{ secret: string }>("/staff/webhooks", { method: "POST", json: { url, description, events } });
      setSecret(r.secret);
      setUrl("");
      setDescription("");
      reload();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Couldn't add the webhook.");
    }
  };
  const test = async (id: string) => {
    const r = await api<{ status: number | null; error: string | null }>(`/staff/webhooks/${id}/test`, { method: "POST" });
    setTestResult((t) => ({ ...t, [id]: r.error ? `Failed: ${r.error}` : `Delivered (HTTP ${r.status})` }));
    reload();
  };
  const toggle = (ev: string) => setEvents((cur) => (ev === "*" ? ["*"] : cur.includes(ev) ? cur.filter((x) => x !== ev) : [...cur.filter((x) => x !== "*"), ev]));

  return (
    <section className="card stack">
      <div>
        <h2>Webhooks</h2>
        <p className="small muted" style={{ margin: "6px 0 0" }}>
          POST a signed JSON event to another system when items change. Verify the <code className="mono">X-DL-Signature</code> header (HMAC-SHA256 of the body with the
          signing secret).
        </p>
      </div>
      <ErrorNote message={error} />
      {secret && (
        <div className="notice stack" role="status">
          <strong>Signing secret (shown once):</strong>
          <div className="keybox">{secret}</div>
          <div className="row">
            <button className="btn btn-small" onClick={() => navigator.clipboard.writeText(secret)}>Copy</button>
            <button className="btn btn-small" onClick={() => setSecret(null)}>Done</button>
          </div>
        </div>
      )}
      {data && data.webhooks.length > 0 && (
        <ul className="list-plain">
          {data.webhooks.map((h) => (
            <li key={h.id} className="stack" style={{ gap: 4 }}>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <span>
                  <span className="mono small">{h.url}</span>
                  {h.description && <span className="small muted"> · {h.description}</span>}
                </span>
                <span className="row" style={{ gap: 6 }}>
                  <button className="btn btn-small" onClick={() => test(h.id)}>Send test</button>
                  <button className="btn btn-small" onClick={() => api(`/staff/webhooks/${h.id}`, { method: "PATCH", json: { active: !h.active } }).then(reload)}>
                    {h.active ? "Pause" : "Resume"}
                  </button>
                  <button className="btn btn-small btn-danger" onClick={() => confirm("Delete this webhook?") && api(`/staff/webhooks/${h.id}`, { method: "DELETE" }).then(reload)}>
                    Delete
                  </button>
                </span>
              </div>
              <span className="small muted">
                Events: {h.events.join(", ")} · {h.active ? "active" : "paused"} ·{" "}
                {h.last_delivery_at ? `last delivery ${timeAgo(h.last_delivery_at)} ${h.last_error ? `failed (${h.last_error})` : `HTTP ${h.last_status}`}` : "no deliveries yet"}
                {testResult[h.id] && ` · ${testResult[h.id]}`}
              </span>
            </li>
          ))}
        </ul>
      )}
      <form className="stack" onSubmit={add}>
        <div className="grid grid-2">
          <div className="field">
            <label htmlFor="wh-url">Endpoint URL (https)</label>
            <input id="wh-url" type="text" required placeholder="https://example.com/hooks/dl" value={url} onChange={(e) => setUrl(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="wh-desc">Description</label>
            <input id="wh-desc" type="text" maxLength={200} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="label">Events</legend>
          <div className="row" style={{ gap: 4 }}>
            {["*", ...(data?.events ?? [])].map((ev) => (
              <label key={ev} className="check" style={{ marginRight: 12 }}>
                <input type="checkbox" checked={events.includes(ev)} onChange={() => toggle(ev)} /> {ev === "*" ? "All events" : ev}
              </label>
            ))}
          </div>
        </fieldset>
        {err && <p className="error-text" role="alert">{err}</p>}
        <div>
          <button className="btn" disabled={!url || events.length === 0}>Add webhook</button>
        </div>
      </form>
    </section>
  );
}

interface DiscordChannel {
  id: string;
  name: string;
  url: string;
  events: string[];
  active: number;
  last_status: number | null;
  last_error: string | null;
  last_delivery_at: string | null;
}

function Discord() {
  const { data, error, reload } = useApi<{ channels: DiscordChannel[]; events: Record<string, string> }>("/staff/discord");
  const events = data?.events ?? {};
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [picked, setPicked] = useState<string[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, string>>({});
  const chosen = picked ?? Object.keys(events);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      await api("/staff/discord", { method: "POST", json: { name, url, events: chosen } });
      setName("");
      setUrl("");
      setPicked(null);
      reload();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Couldn't add the channel.");
    }
  };
  const test = async (id: string) => {
    const r = await api<{ status: number | null; error: string | null }>(`/staff/discord/${id}/test`, { method: "POST" });
    setTestResult((t) => ({ ...t, [id]: r.error ? `Failed: ${r.error}` : "Sent. Check the channel." }));
    reload();
  };
  const toggleEvent = async (ch: DiscordChannel, ev: string) => {
    const next = ch.events.includes(ev) ? ch.events.filter((x) => x !== ev) : [...ch.events, ev];
    if (next.length === 0) return;
    await api(`/staff/discord/${ch.id}`, { method: "PATCH", json: { events: next } });
    reload();
  };

  return (
    <section className="card stack" data-tour="settings-discord">
      <div>
        <h2>Discord</h2>
        <p className="small muted" style={{ margin: "6px 0 0" }}>
          Post new tickets, in-app feedback, customer replies, assignments and urgent items to Discord. In Discord: channel settings → Integrations → Webhooks → New Webhook
          → Copy Webhook URL. Link your Discord ID under <Link href="/account">Your account</Link> to be @mentioned when something is assigned to you.
        </p>
      </div>
      <ErrorNote message={error} />
      {data && data.channels.length > 0 && (
        <ul className="list-plain">
          {data.channels.map((ch) => (
            <li key={ch.id} className="stack" style={{ gap: 6 }}>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <span>
                  <strong>{ch.name}</strong> <span className="mono small muted">{ch.url}</span>
                </span>
                <span className="row" style={{ gap: 6 }}>
                  <button className="btn btn-small" onClick={() => test(ch.id)}>Send test</button>
                  <button className="btn btn-small" onClick={() => api(`/staff/discord/${ch.id}`, { method: "PATCH", json: { active: !ch.active } }).then(reload)}>
                    {ch.active ? "Pause" : "Resume"}
                  </button>
                  <button className="btn btn-small btn-danger" onClick={() => confirm(`Remove ${ch.name}?`) && api(`/staff/discord/${ch.id}`, { method: "DELETE" }).then(reload)}>
                    Remove
                  </button>
                </span>
              </div>
              <div className="row" style={{ gap: 4 }} role="group" aria-label={`Events for ${ch.name}`}>
                {Object.entries(events).map(([key, label]) => (
                  <label key={key} className="check" style={{ marginRight: 10 }}>
                    <input type="checkbox" checked={ch.events.includes(key)} onChange={() => toggleEvent(ch, key)} /> {label}
                  </label>
                ))}
              </div>
              <span className="small muted">
                {ch.active ? "Active" : "Paused"} ·{" "}
                {ch.last_delivery_at ? `last message ${timeAgo(ch.last_delivery_at)} ${ch.last_error ? `failed (${ch.last_error})` : "delivered"}` : "nothing sent yet"}
                {testResult[ch.id] && ` · ${testResult[ch.id]}`}
              </span>
            </li>
          ))}
        </ul>
      )}
      <form className="stack" onSubmit={add}>
        <div className="grid grid-2">
          <div className="field">
            <label htmlFor="dc-name">Channel name</label>
            <input id="dc-name" type="text" required maxLength={80} placeholder="#support" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="dc-url">Discord webhook URL</label>
            <input id="dc-url" type="text" required placeholder="https://discord.com/api/webhooks/…" value={url} onChange={(e) => setUrl(e.target.value)} autoComplete="off" />
          </div>
        </div>
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="label">Post when</legend>
          <div className="row" style={{ gap: 4 }}>
            {Object.entries(events).map(([key, label]) => (
              <label key={key} className="check" style={{ marginRight: 10 }}>
                <input type="checkbox" checked={chosen.includes(key)} onChange={() => setPicked(chosen.includes(key) ? chosen.filter((x) => x !== key) : [...chosen, key])} /> {label}
              </label>
            ))}
          </div>
        </fieldset>
        {err && <p className="error-text" role="alert">{err}</p>}
        <div>
          <button className="btn" disabled={!name || !url || chosen.length === 0}>Add channel</button>
        </div>
      </form>
    </section>
  );
}

function SampleData() {
  const { data, reload } = useApi<{ available: boolean; shown: boolean; summary?: Record<string, number> }>("/staff/sample-data");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!data?.available) return null;
  const s = data.summary;
  const toggle = async () => {
    if (data.shown && !confirm("Hide the sample data? This removes the sample customers and everything attached to them. Real data isn't affected.")) return;
    setBusy(true);
    setErr(null);
    try {
      await api("/staff/sample-data", { method: "PUT", json: { shown: !data.shown } });
      // Every page's numbers change, so start fresh.
      window.location.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Couldn't change sample data.");
      setBusy(false);
      reload();
    }
  };
  return (
    <section className="card stack" data-tour="settings-sample-data">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <div>
          <h2>Sample data</h2>
          <p className="small muted" style={{ margin: "6px 0 0" }}>
            {s ? `${s.customers} fictional customers with ${s.items} work items, ${s.projects} projects and ${s.invoices} invoices` : "Fictional customers and work"}, for
            trying the portal out. Not available in production.
          </p>
        </div>
        <button className={data.shown ? "btn" : "btn btn-primary"} onClick={toggle} disabled={busy} aria-pressed={data.shown}>
          {busy ? "Working…" : data.shown ? "Hide sample data" : "Show sample data"}
        </button>
      </div>
      <p className="small" style={{ margin: 0 }} role="status">
        {data.shown ? "Sample data is showing. Sample customers end in “(sample)” and their invoice numbers start with SAMPLE-, so they're easy to tell apart." : "Sample data is hidden."}
      </p>
      {err && <p className="error-text" role="alert">{err}</p>}
    </section>
  );
}

interface ServiceKeyRow {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  created_by: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

const SCOPE_LABELS: Record<string, string> = {
  "customers:read": "Read customers",
  "customers:write": "Create/update customers",
  "items:read": "Read work items",
  "items:write": "Update work items",
  "inbound:write": "Submit inbound requests",
};

function ServiceKeys() {
  const { data, error, reload } = useApi<{ keys: ServiceKeyRow[]; scopes: string[] }>("/staff/service-keys");
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [created, setCreated] = useState<{ key: string; baseUrl: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const create = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      setCreated(await api<{ key: string; baseUrl: string }>("/staff/service-keys", { method: "POST", json: { name, scopes } }));
      setName("");
      setScopes([]);
      reload();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Couldn't create the key.");
    }
  };
  const active = data?.keys.filter((k) => !k.revoked_at) ?? [];
  return (
    <section className="card stack" data-tour="settings-service-keys">
      <div>
        <h2>Service keys</h2>
        <p className="small muted" style={{ margin: "6px 0 0" }}>
          For other systems (Crumb, forms, scripts) to call the portal's API as an app, not a person. Each key gets only the permissions you tick and can't reach staff settings.
          Machine-readable API description: <a href="/api/v1/openapi.json" target="_blank" rel="noreferrer">/api/v1/openapi.json</a>.
        </p>
      </div>
      <ErrorNote message={error} />
      {created && (
        <div className="notice stack" role="status">
          <strong>Copy this key into the other system now. It won't be shown again.</strong>
          <div className="keybox">{created.key}</div>
          <span className="small">
            Base URL: <code className="mono">{created.baseUrl}</code>
          </span>
          <div className="row">
            <button className="btn btn-small" onClick={() => navigator.clipboard.writeText(created.key)}>
              Copy key
            </button>
            <button className="btn btn-small" onClick={() => setCreated(null)}>
              Done
            </button>
          </div>
        </div>
      )}
      {active.length > 0 && (
        <ul className="list-plain">
          {active.map((k) => (
            <li key={k.id} className="row" style={{ justifyContent: "space-between" }}>
              <span>
                <strong>{k.name}</strong> <span className="mono small muted">dls_{k.prefix}…</span>
                <br />
                <span className="small muted">
                  {k.scopes.map((s) => SCOPE_LABELS[s] ?? s).join(" · ")} · {k.last_used_at ? `used ${timeAgo(k.last_used_at)}` : "never used"}
                </span>
              </span>
              <button
                className="btn btn-small btn-danger"
                onClick={() => confirm(`Revoke ${k.name}? Anything using it stops working.`) && api(`/staff/service-keys/${k.id}`, { method: "DELETE" }).then(reload)}
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="stack" onSubmit={create}>
        <div className="field">
          <label htmlFor="sk-name">Name</label>
          <input id="sk-name" type="text" required maxLength={80} placeholder="e.g. Crumb (production)" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="label">Allowed to</legend>
          <div className="row" style={{ gap: 4 }}>
            {(data?.scopes ?? []).map((s) => (
              <label key={s} className="check" style={{ marginRight: 10 }}>
                <input type="checkbox" checked={scopes.includes(s)} onChange={() => setScopes(scopes.includes(s) ? scopes.filter((x) => x !== s) : [...scopes, s])} />{" "}
                {SCOPE_LABELS[s] ?? s}
              </label>
            ))}
          </div>
        </fieldset>
        {err && <p className="error-text" role="alert">{err}</p>}
        <div>
          <button className="btn" disabled={!name || scopes.length === 0}>
            Create key
          </button>
        </div>
      </form>
    </section>
  );
}
